import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// @ts-nocheck
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { safeCssUrl } from '../../common/utils/safeCssUrl';

// Polling cadence — same order of magnitude as Habbo's trade-window refresh.
// Long enough to not hammer the server, short enough that "the other side
// added an item" feels responsive.
const POLL_INTERVAL_MS = 4000;
/**
 * Habbo-style trade window. Opens via the "Trade" entry on a user's profile
 * controls dropdown (or auto-opens when the user clicks a trade-request
 * notification).
 *
 * Lifecycle:
 *   - `oninit` POSTs to /trades to open or resume the trade with the target
 *     user. Server returns the canonical state. We mirror it in component
 *     state and start polling.
 *   - `onremove` clears the polling interval. Closing the modal does NOT
 *     cancel the trade — the user can come back later and pick it up.
 *   - Each user-initiated change (add/remove item, set points, accept,
 *     cancel) hits the server immediately; the server response replaces
 *     local state so we never drift.
 *
 * Attrs:
 *   - target?: User      → counterparty (when opened from a profile)
 *   - tradeId?: number   → existing trade id (when reopened from a notification)
 */
export default class TradeModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "trade", null);
    _defineProperty(this, "err", '');
    _defineProperty(this, "busy", null);
    _defineProperty(this, "pollHandle", null);
    _defineProperty(this, "pickerOpen", false);
    _defineProperty(this, "pointsDraft", '0');
    // Countdown state for the "5...4...3...2...1... → finalize" overlay that
    // runs once BOTH sides have accepted while the trade is still pending.
    // We hold a wall-clock deadline (`countdownEndsAt`) rather than a tick
    // counter so the displayed number stays accurate even if the browser
    // throttles our setInterval (background tabs, slow devices). The handle
    // is the redraw timer we kick off when the countdown starts and clear
    // when it ends, gets cancelled (one side un-accepts), or the modal
    // unmounts.
    _defineProperty(this, "countdownEndsAt", null);
    _defineProperty(this, "countdownHandle", null);
    // True once we've fired POST /finalize for the current accept-both
    // session. Either side's client races to call /finalize; the endpoint is
    // idempotent server-side, but firing twice in the same tab is wasteful.
    _defineProperty(this, "finalizeFired", false);
  }
  className() {
    // Plain `PointSystemTradeModal` — no Flarum modifier class. The core
    // only ships `Modal--small` and `Modal--large` (no `--medium`); using a
    // bogus modifier risked falling into a layout state where the modal
    // rendered behind the `.ModalManager-invisibleBackdrop` sibling (user
    // bug report: "o modal fica por baixo de invisibleBackdrop"). Our own
    // CSS in less/forum.less owns the trade-modal width + presentation now.
    return 'PointSystemTradeModal';
  }
  title() {
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trade.' + k, v);
    if (!this.trade) return t('title_loading');
    const youAre = this.trade.youAre;
    const other = youAre === 'initiator' ? this.trade.recipient : this.trade.initiator;
    return t('title_with', {
      name: (other == null ? void 0 : other.displayName) || (other == null ? void 0 : other.username) || '—'
    });
  }
  oninit(vnode) {
    super.oninit(vnode);
    if (this.attrs.tradeId) {
      this.refresh(this.attrs.tradeId);
    } else if (this.attrs.target) {
      var _this$attrs$target$id, _this$attrs$target;
      this.open(Number((_this$attrs$target$id = (_this$attrs$target = this.attrs.target).id) == null ? void 0 : _this$attrs$target$id.call(_this$attrs$target)));
    } else {
      this.err = 'no_target';
      this.loading = false;
    }
  }
  oncreate(vnode) {
    var _super$oncreate;
    (_super$oncreate = super.oncreate) == null || _super$oncreate.call(this, vnode);
    // Final defence against the .ModalManager-invisibleBackdrop covering us.
    // CSS (body:has(.PointSystemTradeModal) .ModalManager-invisibleBackdrop)
    // handles modern browsers, but on a few theme stacking contexts the
    // backdrop is appended AFTER our oncreate runs, OR the host browser
    // lacks `:has()` support entirely. Walk the DOM once on mount and
    // explicitly disable any backdrop currently present. Cheap, idempotent,
    // and harmless if the CSS already neutralised them.
    document.querySelectorAll('.ModalManager-invisibleBackdrop').forEach(el => {
      el.style.display = 'none';
      el.style.pointerEvents = 'none';
    });
  }
  onremove() {
    var _super$onremove;
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    // Cancel any in-flight countdown so it doesn't fire /finalize after
    // the modal unmounts — Mithril would warn about setState-after-unmount
    // and we'd hit the server with a request whose response nobody reads.
    this.stopCountdown();
    // Restore the backdrops we forced off so OTHER modals opened later
    // (award-points, confirm-purchase, etc.) keep their dismissal-on-click
    // affordance.
    document.querySelectorAll('.ModalManager-invisibleBackdrop').forEach(el => {
      el.style.removeProperty('display');
      el.style.removeProperty('pointer-events');
    });
    (_super$onremove = super.onremove) == null || _super$onremove.call(this);
  }
  startPolling() {
    if (this.pollHandle) return;
    this.pollHandle = setInterval(() => {
      if (!this.trade || this.trade.status !== 'pending') {
        clearInterval(this.pollHandle);
        this.pollHandle = null;
        return;
      }
      this.refresh(Number(this.trade.id));
    }, POLL_INTERVAL_MS);
  }
  async open(recipientId) {
    this.loading = true;
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'POST',
        url: "".concat(apiUrl, "/point-system/trades"),
        body: {
          recipientId
        }
      });
      this.applyState(res == null ? void 0 : res.data);
      this.startPolling();
    } catch (e) {
      var _e$response;
      this.err = (e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) || 'open_failed';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  async refresh(tradeId) {
    try {
      var _this$trade;
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'GET',
        url: "".concat(apiUrl, "/point-system/trades/").concat(tradeId)
      });
      this.applyState(res == null ? void 0 : res.data);
      if (((_this$trade = this.trade) == null ? void 0 : _this$trade.status) === 'pending') this.startPolling();
    } catch (e) {
      var _e$response2;
      this.err = (e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail) || 'refresh_failed';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  applyState(trade) {
    var _document$activeEleme;
    if (!trade) return;
    this.trade = trade;
    // Only sync the points input from server when the user isn't actively
    // editing — otherwise we'd snap their typing back as soon as the poll
    // returns the previous value.
    if (((_document$activeEleme = document.activeElement) == null ? void 0 : _document$activeEleme.tagName) !== 'INPUT' || !document.activeElement.classList.contains('ps-trade-points')) {
      const mine = trade.youAre === 'initiator' ? trade.initiatorPoints : trade.recipientPoints;
      this.pointsDraft = String(mine != null ? mine : 0);
    }
    this.err = '';

    // Drive the 5-second pre-finalize countdown off of every state update.
    // Three transitions matter:
    //  - both-just-accepted: kick off the countdown if not already running.
    //  - one-side-un-accepted: cancel any in-flight countdown so the trade
    //    doesn't auto-finalize after the user backed out.
    //  - completed/cancelled: clear countdown plumbing; the trade is final.
    this.reconcileCountdown();
  }
  reconcileCountdown() {
    const tr = this.trade;
    if (!tr) {
      this.stopCountdown();
      return;
    }
    if (tr.status !== 'pending') {
      // Completed or cancelled — done. Reset the "fired" flag so a future
      // re-opened pending trade can countdown again.
      this.stopCountdown();
      this.finalizeFired = false;
      return;
    }
    const both = !!tr.initiatorAccepted && !!tr.recipientAccepted;
    if (both) {
      this.startCountdown();
    } else {
      // Either side un-accepted between the start of the countdown and
      // now — abort. Server-side, the trade also stays pending; nothing
      // to revert.
      this.stopCountdown();
      this.finalizeFired = false;
    }
  }
  startCountdown() {
    if (this.countdownHandle || this.countdownEndsAt) return;
    // Wall-clock deadline so the displayed digit stays correct under tab
    // throttling. 5 seconds = 5000 ms; we re-tick at 200 ms so the visible
    // digit transitions feel smooth without burning CPU.
    this.countdownEndsAt = Date.now() + 5000;
    this.countdownHandle = setInterval(() => {
      const remaining = this.countdownEndsAt - Date.now();
      if (remaining <= 0) {
        this.stopCountdown();
        if (!this.finalizeFired) {
          this.finalizeFired = true;
          this.finalize();
        }
      } else {
        m.redraw();
      }
    }, 200);
    m.redraw();
  }
  stopCountdown() {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = null;
    }
    this.countdownEndsAt = null;
  }
  countdownRemainingSeconds() {
    if (!this.countdownEndsAt) return null;
    const ms = this.countdownEndsAt - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 1000);
  }
  async finalize() {
    if (!this.trade) return;
    this.busy = 'finalize';
    m.redraw();
    try {
      var _this$trade2;
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'POST',
        url: "".concat(apiUrl, "/point-system/trades/").concat(this.trade.id, "/finalize"),
        body: {}
      });
      this.applyState(res == null ? void 0 : res.data);
      if (((_this$trade2 = this.trade) == null ? void 0 : _this$trade2.status) === 'completed') {
        this.reconcileLocalOwnership();
        app.alerts.show({
          type: 'success'
        }, app.translator.trans('ramon-point-system.forum.trade.completed_alert'));
      }
    } catch (e) {
      var _e$response3;
      // Inspect the response payload — `errors[0].detail` is the
      // JSON-encoded validation attributes object (controllers serialize
      // ValidationException attrs via json_encode). Parse it and surface
      // a localized message for the well-known codes.
      const rawDetail = e == null || (_e$response3 = e.response) == null || (_e$response3 = _e$response3.errors) == null || (_e$response3 = _e$response3[0]) == null ? void 0 : _e$response3.detail;
      let code = '';
      try {
        var _parsed$trade;
        const parsed = typeof rawDetail === 'string' ? JSON.parse(rawDetail) : rawDetail;
        code = String((_parsed$trade = parsed == null ? void 0 : parsed.trade) != null ? _parsed$trade : '');
      } catch (_unused) {
        code = '';
      }
      if (code === 'recipient_already_owns_item') {
        this.err = app.translator.trans('ramon-point-system.forum.trade.error_recipient_owns_item');
      } else if (code === 'item_equipped') {
        this.err = app.translator.trans('ramon-point-system.forum.trade.error_item_equipped');
      } else if (code === 'not_both_accepted') {
        this.err = app.translator.trans('ramon-point-system.forum.trade.error_not_both_accepted');
      } else if (code === 'initiator_insufficient_points' || code === 'recipient_insufficient_points') {
        this.err = app.translator.trans('ramon-point-system.forum.trade.error_insufficient_points');
      } else if (code === 'item_unavailable') {
        this.err = app.translator.trans('ramon-point-system.forum.trade.error_item_unavailable');
      } else {
        this.err = rawDetail || 'finalize_failed';
      }
      // Allow another attempt — the countdown is over, so if the user
      // adjusts the offer and re-accepts, a fresh countdown will kick in.
      this.finalizeFired = false;
      // DON'T `this.refresh(...)` immediately here: the server has already
      // reset both `accepted` flags in its catch handler (see
      // TradeRepository::execute), and our `applyState()` clears `this.err`
      // every time it runs. Forcing a refresh now would wipe the error
      // message before the user gets a chance to read it. The polling
      // interval (every 4s) will pick up the new state shortly anyway, by
      // which time the user has seen the explanation and either fixed the
      // offer or closed the modal.
    } finally {
      this.busy = null;
      m.redraw();
    }
  }
  view() {
    var _app$session$user$att, _app$session$user;
    if (this.loading) return m(LoadingIndicator, null);
    if (this.err === 'no_target') {
      return m("div", {
        className: "Modal-body"
      }, app.translator.trans('ramon-point-system.forum.trade.error_no_target'));
    }
    if (!this.trade) {
      return m("div", {
        className: "Modal-body"
      }, app.translator.trans('ramon-point-system.forum.trade.error_load'));
    }
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trade.' + k, v);
    const youAre = this.trade.youAre;
    const theirPoints = youAre === 'initiator' ? this.trade.recipientPoints : this.trade.initiatorPoints;
    const yourAccept = youAre === 'initiator' ? this.trade.initiatorAccepted : this.trade.recipientAccepted;
    const theirAccept = youAre === 'initiator' ? this.trade.recipientAccepted : this.trade.initiatorAccepted;
    const them = youAre === 'initiator' ? this.trade.recipient : this.trade.initiator;
    const me = youAre === 'initiator' ? this.trade.initiator : this.trade.recipient;
    const yourItems = this.itemsForOwner(me == null ? void 0 : me.id);
    const theirItems = this.itemsForOwner(them == null ? void 0 : them.id);
    const isFinal = this.trade.status === 'completed' || this.trade.status === 'cancelled';
    const balance = Number((_app$session$user$att = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.attribute('pointBalance')) != null ? _app$session$user$att : 0);
    return m("div", {
      className: "Modal-body PointSystemTradeModal-body"
    }, m("div", {
      className: "PointSystemTradeModal-banner is-".concat(this.trade.status)
    }, this.trade.status === 'completed' && m("span", null, m("i", {
      className: "fas fa-check-circle"
    }), " ", t('status_completed')), this.trade.status === 'cancelled' && m("span", null, m("i", {
      className: "fas fa-times-circle"
    }), " ", t('status_cancelled')), this.trade.status === 'pending' && yourAccept && theirAccept && (() => {
      const remaining = this.countdownRemainingSeconds();
      return remaining !== null && remaining > 0 ? m("span", {
        className: "PointSystemTradeModal-countdown"
      }, m("span", {
        className: "PointSystemTradeModal-countdown-digit"
      }, remaining), m("span", {
        className: "PointSystemTradeModal-countdown-text"
      }, t('finalizing_in'))) : m("span", null, m("i", {
        className: "fas fa-hourglass"
      }), " ", t('status_both_accepted'));
    })(), this.trade.status === 'pending' && (!yourAccept || !theirAccept) && m("span", null, m("i", {
      className: "fas fa-handshake"
    }), " ", t('status_pending'))), m("div", {
      className: "PointSystemTradeModal-panels"
    }, m("div", {
      className: "PointSystemTradeModal-panel ".concat(yourAccept ? 'is-accepted' : '')
    }, m("h3", null, t('your_offer')), m("div", {
      className: "PointSystemTradeModal-items"
    }, yourItems.map(it => m("div", {
      className: "PointSystemTradeModal-itemChip",
      key: "y-".concat(it.itemType, "-").concat(it.itemId)
    }, this.renderItemThumb(it, me), m("span", {
      className: "PointSystemTradeModal-itemChip-name"
    }, it.name || "".concat(it.itemType, "#").concat(it.itemId)), !isFinal && m("button", {
      type: "button",
      className: "PointSystemTradeModal-itemChip-remove",
      title: t('remove_item'),
      onclick: () => this.removeItem(it)
    }, m("i", {
      className: "fas fa-times"
    })))), yourItems.length === 0 && m("p", {
      className: "helpText"
    }, t('no_items_yet'))), !isFinal && m(Button, {
      className: "Button Button--link PointSystemTradeModal-addItem",
      onclick: () => this.pickerOpen = !this.pickerOpen
    }, m("i", {
      className: "fas fa-plus"
    }), " ", t('add_item')), this.pickerOpen && !isFinal && this.renderItemPicker(yourItems, me), m("div", {
      className: "PointSystemTradeModal-points"
    }, m("label", null, t('your_points')), m("input", {
      type: "number",
      min: "0",
      max: balance,
      className: "FormControl ps-trade-points",
      value: this.pointsDraft,
      disabled: isFinal,
      oninput: e => this.pointsDraft = e.target.value,
      onblur: () => this.commitPoints()
    }), m("small", {
      className: "helpText"
    }, t('balance'), ": ", m("strong", null, balance.toLocaleString())))), m("div", {
      className: "PointSystemTradeModal-panel is-readonly ".concat(theirAccept ? 'is-accepted' : '')
    }, m("h3", null, t('their_offer', {
      name: (them == null ? void 0 : them.displayName) || (them == null ? void 0 : them.username) || '—'
    })), m("div", {
      className: "PointSystemTradeModal-items"
    }, theirItems.map(it => m("div", {
      className: "PointSystemTradeModal-itemChip is-readonly",
      key: "t-".concat(it.itemType, "-").concat(it.itemId)
    }, this.renderItemThumb(it, them), m("span", {
      className: "PointSystemTradeModal-itemChip-name"
    }, it.name || "".concat(it.itemType, "#").concat(it.itemId)))), theirItems.length === 0 && m("p", {
      className: "helpText"
    }, t('no_items_yet'))), m("div", {
      className: "PointSystemTradeModal-points"
    }, m("label", null, t('their_points')), m("div", {
      className: "PointSystemTradeModal-readonly"
    }, m("strong", null, Number(theirPoints || 0).toLocaleString()))))), !isFinal && m("div", {
      className: "PointSystemTradeModal-actions"
    }, m(Button, {
      className: "Button ".concat(yourAccept ? 'Button--primary' : ''),
      loading: this.busy === 'accept',
      onclick: () => this.toggleAccept()
    }, m("i", {
      className: "fas ".concat(yourAccept ? 'fa-check-double' : 'fa-check')
    }), " ", yourAccept ? t('unaccept') : t('accept')), m(Button, {
      className: "Button",
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        this.stopCountdown();
        if (this.pollHandle) {
          clearInterval(this.pollHandle);
          this.pollHandle = null;
        }
        app.modal.close();
      }
    }, m("i", {
      className: "fas fa-times-circle"
    }), " ", t('close_keep')), m(Button, {
      className: "Button Button--danger",
      loading: this.busy === 'cancel',
      onclick: () => this.cancel()
    }, m("i", {
      className: "fas fa-trash-alt"
    }), " ", t('cancel'))), !isFinal && m("p", {
      className: "PointSystemTradeModal-persistHint"
    }, m("i", {
      className: "fas fa-cloud"
    }), " ", t('persist_hint')), isFinal && m("div", {
      className: "PointSystemTradeModal-actions PointSystemTradeModal-actions--final"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        this.stopCountdown();
        if (this.pollHandle) {
          clearInterval(this.pollHandle);
          this.pollHandle = null;
        }
        app.modal.close();
      }
    }, m("i", {
      className: "fas fa-check"
    }), " ", t('close_final'))), this.err && this.err !== 'no_target' && m("p", {
      className: "PointSystemTradeModal-error"
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), " ", t('error_' + this.err) || this.err));
  }
  renderItemPicker(yourItems, owner) {
    var _app$session$user2;
    const owned = ((_app$session$user2 = app.session.user) == null ? void 0 : _app$session$user2.attribute('ownedDecorationIds')) || [];
    const onTable = new Set(yourItems.map(i => "".concat(i.itemType, ":").concat(i.itemId)));

    // Itens atualmente equipados pelo ator não podem ser oferecidos —
    // o servidor recusaria com `item_equipped`, e exibir um picker que
    // engaja-erra confunde o usuário. Filtramos por (tipo, id) batendo
    // contra o `current_*_decoration_id` de cada família.
    const me = app.session.user;
    const equippedSet = new Set();
    if (me) {
      const pairs = [['avatar_decoration', me.attribute('equippedAvatarDecorationId')], ['name_decoration', me.attribute('equippedNameDecorationId')], ['cover_decoration', me.attribute('equippedCoverDecorationId')], ['title_decoration', me.attribute('equippedTitleDecorationId')], ['post_highlight_decoration', me.attribute('equippedPostHighlightDecorationId')]];
      for (const _ref of pairs) {
        var _ref2 = _slicedToArray(_ref, 2);
        const type = _ref2[0];
        const id = _ref2[1];
        const n = Number(id != null ? id : 0);
        if (n > 0) equippedSet.add("".concat(type, ":").concat(n));
      }
    }
    const candidates = owned.map(o => this.resolveItem(o.type, Number(o.id))).filter(it => it && !onTable.has("".concat(it.itemType, ":").concat(it.itemId))).filter(it => !equippedSet.has("".concat(it.itemType, ":").concat(it.itemId)));
    return m("div", {
      className: "PointSystemTradeModal-picker"
    }, candidates.length === 0 ? m("p", {
      className: "helpText"
    }, app.translator.trans('ramon-point-system.forum.trade.picker_empty')) : candidates.map(it => m("button", {
      type: "button",
      className: "PointSystemTradeModal-pickerItem",
      key: "pick-".concat(it.itemType, "-").concat(it.itemId),
      onclick: () => this.addItem(it)
    }, this.renderItemThumb(it, owner), m("span", null, it.name))));
  }

  /**
   * Render a realistic preview of the item using the OWNER's actual
   * profile (avatar URL, display name). This way the user sees exactly
   * how the decoration will look on the party that owns it:
   *
   *   - avatar_decoration: owner's avatar with the decoration frame
   *     overlaid (same `.ps-avatar-deco-wrap` / `.ps-avatar-deco`
   *     classes the real post avatar uses).
   *   - cover_decoration: the banner image with the owner's avatar
   *     overlaid as a small profile pic (mini-hero shape).
   *   - name_decoration: the owner's display name rendered with the
   *     decoration's CSS class.
   *   - title_decoration: the title text (already preview-correct;
   *     title text is fixed at the decoration level, not per-user).
   *   - post_highlight_decoration: the owner's display name inside a
   *     post-hl-styled box to suggest the post-border treatment.
   */
  renderItemThumb(it, owner) {
    var _owner$avatarUrl;
    const ownerName = ((owner == null ? void 0 : owner.displayName) || (owner == null ? void 0 : owner.username) || 'Aa').slice(0, 32);
    const avatarUrl = (_owner$avatarUrl = owner == null ? void 0 : owner.avatarUrl) != null ? _owner$avatarUrl : null;
    if (it.itemType === 'avatar_decoration') {
      const frameSrc = it.imageUrl || it.imagePath || '';
      const frameUrl = frameSrc ? this.resolveAsset(frameSrc) : '';
      const safeFrameUrl = safeCssUrl(frameUrl);
      return m("span", {
        className: "ps-avatar-deco-wrap PointSystemTradeModal-itemThumb PointSystemTradeModal-itemThumb--avatar"
      }, avatarUrl ? m("img", {
        className: "Avatar",
        src: avatarUrl,
        alt: ""
      }) : m("span", {
        className: "Avatar PointSystemTradeModal-itemThumb-avatarFallback"
      }, ownerName.charAt(0).toUpperCase()), frameUrl && m("span", {
        "aria-hidden": "true",
        className: "ps-avatar-deco",
        style: "background-image: url(\"".concat(safeFrameUrl, "\");")
      }));
    }
    if (it.itemType === 'cover_decoration') {
      const coverSrc = it.imageUrl || it.imagePath || '';
      const coverUrl = coverSrc ? this.resolveAsset(coverSrc) : '';
      const safeCoverUrl = coverUrl.replace(/"/g, '%22');
      const coverStyle = coverUrl ? "background-image: url(\"".concat(safeCoverUrl, "\"); background-size: cover; background-position: center;") : '';
      return m("span", {
        className: "PointSystemTradeModal-itemThumb PointSystemTradeModal-itemThumb--cover",
        style: coverStyle
      }, avatarUrl && m("img", {
        className: "PointSystemTradeModal-itemThumb-coverAvatar Avatar",
        src: avatarUrl,
        alt: ""
      }));
    }
    if (it.itemType === 'name_decoration' && it.slug) {
      const slug = String(it.slug).replace(/[^a-zA-Z0-9_-]/g, '');
      return m("span", {
        className: "ps-name-preview ps-name-".concat(slug, " PointSystemTradeModal-itemThumb PointSystemTradeModal-itemThumb--name")
      }, ownerName);
    }
    if (it.itemType === 'title_decoration' && it.titleText) {
      const slug = String(it.slug || '').replace(/[^a-zA-Z0-9_-]/g, '');
      const safe = String(it.color || '').replace(/[<>"';]/g, '');
      const style = safe ? "--ps-title-color:".concat(safe, ";") : '';
      return m("span", {
        className: "ps-title-preview ps-title-".concat(slug, " PointSystemTradeModal-itemThumb"),
        style: style
      }, it.titleText);
    }
    if (it.itemType === 'post_highlight_decoration' && it.slug) {
      const slug = String(it.slug).replace(/[^a-zA-Z0-9_-]/g, '');
      return m("span", {
        className: "ps-posthl-preview ps-posthl-".concat(slug, " PointSystemTradeModal-itemThumb PointSystemTradeModal-itemThumb--postHl")
      }, ownerName);
    }
    return m("i", {
      className: "fas fa-cube PointSystemTradeModal-itemThumb"
    });
  }
  itemsForOwner(ownerId) {
    if (!ownerId || !this.trade) return [];
    return (this.trade.items || []).filter(it => Number(it.ownerId) === Number(ownerId)).map(it => this.resolveItem(it.itemType, Number(it.itemId))).filter(Boolean);
  }
  resolveItem(type, id) {
    const sources = {
      avatar_decoration: 'pointSystemAvatarDecorations',
      name_decoration: 'pointSystemNameDecorations',
      cover_decoration: 'pointSystemCoverDecorations',
      title_decoration: 'pointSystemTitleDecorations',
      post_highlight_decoration: 'pointSystemPostHighlightDecorations'
    };
    const attr = sources[type];
    if (!attr) return {
      itemType: type,
      itemId: id
    };
    const list = app.forum.attribute(attr) || [];
    const found = list.find(d => Number(d.id) === Number(id));
    if (!found) return {
      itemType: type,
      itemId: id,
      name: "#".concat(id)
    };
    return _objectSpread({
      itemType: type,
      itemId: id
    }, found);
  }
  resolveAsset(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = app.forum.attribute('assetsBaseUrl') || app.forum.attribute('baseUrl') + '/assets';
    return base.replace(/\/+$/, '') + '/' + String(path).replace(/^\/+/, '');
  }
  async addItem(it) {
    if (!this.trade) return;
    const youAre = this.trade.youAre;
    const me = youAre === 'initiator' ? this.trade.initiator : this.trade.recipient;
    const current = this.itemsForOwner(me == null ? void 0 : me.id).map(c => ({
      itemType: c.itemType,
      itemId: c.itemId
    }));
    current.push({
      itemType: it.itemType,
      itemId: it.itemId
    });
    await this.patchOffer({
      items: current
    });
    this.pickerOpen = false;
  }
  async removeItem(it) {
    if (!this.trade) return;
    const youAre = this.trade.youAre;
    const me = youAre === 'initiator' ? this.trade.initiator : this.trade.recipient;
    const current = this.itemsForOwner(me == null ? void 0 : me.id).filter(c => !(c.itemType === it.itemType && c.itemId === it.itemId)).map(c => ({
      itemType: c.itemType,
      itemId: c.itemId
    }));
    await this.patchOffer({
      items: current
    });
  }
  async commitPoints() {
    if (!this.trade) return;
    const youAre = this.trade.youAre;
    const serverPoints = youAre === 'initiator' ? this.trade.initiatorPoints : this.trade.recipientPoints;
    const next = Math.max(0, Number(this.pointsDraft) || 0);
    if (next === Number(serverPoints)) return;
    await this.patchOffer({
      points: next
    });
  }
  async patchOffer(payload) {
    if (!this.trade) return;
    this.busy = 'setPoints';
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'PATCH',
        url: "".concat(apiUrl, "/point-system/trades/").concat(this.trade.id),
        body: payload
      });
      this.applyState(res == null ? void 0 : res.data);
    } catch (e) {
      var _e$response4;
      this.err = (e == null || (_e$response4 = e.response) == null || (_e$response4 = _e$response4.errors) == null || (_e$response4 = _e$response4[0]) == null ? void 0 : _e$response4.detail) || 'patch_failed';
    } finally {
      this.busy = null;
      m.redraw();
    }
  }
  async toggleAccept() {
    if (!this.trade) return;
    // Snapshot whether THIS click is the user setting their side to accepted
    // (vs. un-accepting). The server response replaces local state, so we
    // can't read `yourAccepted` after applyState() to know if it flipped to
    // true on this turn.
    const youAre = this.trade.youAre;
    const wasAccepted = youAre === 'initiator' ? this.trade.initiatorAccepted : this.trade.recipientAccepted;
    this.busy = 'accept';
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'POST',
        url: "".concat(apiUrl, "/point-system/trades/").concat(this.trade.id, "/accept"),
        body: {}
      });
      this.applyState(res == null ? void 0 : res.data);

      // If the trade just completed, refresh the user's owned-items cache
      // so the next reopen of My Decorations reflects the new inventory.
      // The server moves ShopClaim rows so a fresh /api/users/{me} call
      // would update ownedDecorationIds; rather than re-fetching the whole
      // user, we reconcile locally below.
      if (this.trade.status === 'completed') {
        this.reconcileLocalOwnership();
        app.alerts.show({
          type: 'success'
        }, app.translator.trans('ramon-point-system.forum.trade.completed_alert'));
        return;
      }

      // Unblock the user when they JUST accepted and the trade is still
      // pending (other side hasn't accepted yet). Keeping the modal open
      // forces them to either babysit the trade or hit X to close — but
      // either way the server-side state is already saved. Auto-dismiss
      // and surface a "waiting for the other side" toast so they can
      // continue browsing; the trade is reachable from the Trades tab /
      // trade-history page when the other side acts.
      const nowAccepted = youAre === 'initiator' ? this.trade.initiatorAccepted : this.trade.recipientAccepted;
      const theirAccept = youAre === 'initiator' ? this.trade.recipientAccepted : this.trade.initiatorAccepted;
      if (!wasAccepted && nowAccepted && !theirAccept) {
        app.alerts.show({
          type: 'success'
        }, app.translator.trans('ramon-point-system.forum.trade.accepted_waiting_alert'));
        this.hide();
      }
    } catch (e) {
      var _e$response5;
      this.err = (e == null || (_e$response5 = e.response) == null || (_e$response5 = _e$response5.errors) == null || (_e$response5 = _e$response5[0]) == null ? void 0 : _e$response5.detail) || 'accept_failed';
    } finally {
      this.busy = null;
      m.redraw();
    }
  }
  async cancel() {
    if (!this.trade) return;
    if (!confirm(app.translator.trans('ramon-point-system.forum.trade.confirm_cancel'))) return;
    this.busy = 'cancel';
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'POST',
        url: "".concat(apiUrl, "/point-system/trades/").concat(this.trade.id, "/cancel"),
        body: {}
      });
      this.applyState(res == null ? void 0 : res.data);
    } catch (e) {
      var _e$response6;
      this.err = (e == null || (_e$response6 = e.response) == null || (_e$response6 = _e$response6.errors) == null || (_e$response6 = _e$response6[0]) == null ? void 0 : _e$response6.detail) || 'cancel_failed';
    } finally {
      this.busy = null;
      m.redraw();
    }
  }

  /**
   * Update the local user's ownedDecorationIds and pointBalance to reflect a
   * completed trade. Server is authoritative; this is just a UI nudge so
   * the user doesn't have to refresh to see the change.
   */
  reconcileLocalOwnership() {
    var _user$attribute;
    const user = app.session.user;
    if (!user || !this.trade) return;
    const meId = Number(user.id());
    const owned = (user.attribute('ownedDecorationIds') || []).slice();
    const givenAway = this.trade.items.filter(it => Number(it.ownerId) === meId);
    const received = this.trade.items.filter(it => Number(it.ownerId) !== meId);
    const filtered = owned.filter(o => !givenAway.some(g => {
      var _o$quantity;
      if (g.itemType !== o.type || Number(g.itemId) !== Number(o.id)) return false;
      // Backend só apaga a linha quando `quantity` chega a zero — se
      // sobra cópia, o claim continua na lista (apenas com quantity--).
      // Removemos do array local apenas quando NÃO há outra cópia
      // declarada; estoque > 1 sobrevive ao trade.
      return Number((_o$quantity = o.quantity) != null ? _o$quantity : 1) <= 1;
    }));
    for (const r of received) {
      filtered.push({
        type: r.itemType,
        id: r.itemId
      });
    }
    const myPoints = this.trade.youAre === 'initiator' ? this.trade.initiatorPoints : this.trade.recipientPoints;
    const theirPoints = this.trade.youAre === 'initiator' ? this.trade.recipientPoints : this.trade.initiatorPoints;
    const balance = Math.max(0, Number((_user$attribute = user.attribute('pointBalance')) != null ? _user$attribute : 0) - Number(myPoints) + Number(theirPoints));

    /*
     * Limpa o ponteiro `equipped*` do doador quando o item transferido
     * era o equipado. Acompanha a limpeza que o backend faz em
     * TradeRepository::execute(); sem isso o frontend continua exibindo
     * o botão "Equipado" até o próximo refresh completo do user model.
     */
    const equippedPatch = {};
    const equippedMap = {
      avatar_decoration: {
        id: 'equippedAvatarDecorationId',
        extras: ['equippedAvatarDecorationUrl']
      },
      name_decoration: {
        id: 'equippedNameDecorationId',
        extras: ['equippedNameDecorationSlug']
      },
      cover_decoration: {
        id: 'equippedCoverDecorationId',
        extras: ['equippedCoverDecorationUrl']
      },
      title_decoration: {
        id: 'equippedTitleDecorationId',
        extras: ['equippedTitleDecorationSlug', 'equippedTitleDecorationText']
      },
      post_highlight_decoration: {
        id: 'equippedPostHighlightDecorationId',
        extras: ['equippedPostHighlightDecorationSlug']
      }
    };
    for (const g of givenAway) {
      var _user$attribute2;
      const meta = equippedMap[g.itemType];
      if (!meta) continue;
      // Só desequipa quando o estoque local também foi a zero (já refletido em `filtered`).
      const stillOwned = filtered.some(o => o.type === g.itemType && Number(o.id) === Number(g.itemId));
      if (stillOwned) continue;
      if (Number((_user$attribute2 = user.attribute(meta.id)) != null ? _user$attribute2 : 0) === Number(g.itemId)) {
        equippedPatch[meta.id] = null;
        for (const k of meta.extras) equippedPatch[k] = null;
      }
    }
    user.pushAttributes(_objectSpread({
      ownedDecorationIds: filtered,
      pointBalance: balance
    }, equippedPatch));
  }
}
// Every dismissal path is safe — the trade row is persisted on every
// mutation (add item, set points, accept), so closing the window NEVER
// loses the user's progress. Reopening the trade (via the partner's
// profile, the trades page, or a notification) loads the exact same
// pending row back. Allow all three native dismissal affordances:
// Escape, the X button, and a backdrop click. The earlier `false` on
// backdrop click pre-dated the `display: none` we now apply to the
// `.ModalManager-invisibleBackdrop` sibling (see TradeModal.oncreate
// and less/forum.less) — without that backdrop in the DOM, the user
// can't even hit it accidentally; keeping the option enabled is the
// honest default in case a future patch changes the backdrop story.
_defineProperty(TradeModal, "dismissibleOptions", {
  viaEscKey: true,
  viaCloseButton: true,
  viaBackdropClick: true
});
flarum.reg.add('ramon-point-system', 'forum/components/TradeModal', TradeModal);